import {
  EReviewSubmissionStatus,
  type TReviewDueState,
  type TReviewMissingDateField,
  type TReviewMissingProgress,
  type TReviewQueueDateField,
  type TReviewTimeState,
} from '@onesource/schemas';
import type { TReviewExportParams, TReviewMissingParams, TReviewQueueParams } from '@onesource/services';
import { useEffect, useRef, useState } from 'react';
import { useDebounce } from 'usehooks-ts';

import { EMPTY_FILTER_VALUE, type TReviewFilterValue } from './review-filter-panel';
import {
  defaultDateField,
  filtersFromSearch,
  isMissingDateField,
  isSameSearch,
  searchFromState,
  type TReviewDateRange,
  type TReviewListFilterKey,
  type TReviewSearch,
} from './review-search';
import type { TReviewView } from './review-status-tiles';

const PER_PAGE = 20;

/** Which list filters each view uses. Module, form, teacher, search and date are on both. */
const FILTERS_BY_VIEW: Record<'submissions' | 'missing', TReviewListFilterKey[]> = {
  submissions: ['module', 'form', 'teacher', 'reviewTime'],
  missing: ['module', 'form', 'teacher', 'progress', 'due'],
};

const stateFromSearch = (search: TReviewSearch) => {
  const view: TReviewView = search.view ?? EReviewSubmissionStatus.waiting;
  const isMissing = view === 'not_submitted';
  const field =
    search.dateField && isMissingDateField(search.dateField) === isMissing
      ? search.dateField
      : defaultDateField(isMissing);
  return {
    view,
    filters: filtersFromSearch(search),
    searchInput: search.search ?? '',
    date: { field, from: search.dateFrom, to: search.dateTo } as TReviewDateRange,
  };
};

/**
 * The Teacher Review page's state — view, filters, search, date range, page — kept
 * in step with the URL both ways, and turned into the params each request takes.
 */
export const useReviewPageState = (urlSearch: TReviewSearch, onSearchChange: (search: TReviewSearch) => void) => {
  const initial = useRef(stateFromSearch(urlSearch)).current;
  const [view, setViewState] = useState<TReviewView>(initial.view);
  const [filters, setFilters] = useState(initial.filters);
  const [searchInput, setSearchInput] = useState(initial.searchInput);
  const [date, setDate] = useState<TReviewDateRange>(initial.date);
  const [page, setPage] = useState(1);
  const search = useDebounce(searchInput, 300).trim();
  const isMissingView = view === 'not_submitted';

  // State → URL (replace, so the back button leaves the page rather than undoing filters).
  const lastWritten = useRef<TReviewSearch>({});
  useEffect(() => {
    const activeKeys = FILTERS_BY_VIEW[view === 'not_submitted' ? 'missing' : 'submissions'];
    const next = searchFromState({ view, search, filters, activeKeys, date });
    lastWritten.current = next;
    onSearchChange(next);
    // onSearchChange is a new function each render; the state is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, search, filters, date]);

  // URL → state, when something else changed it (a reminder link opened on this page).
  useEffect(() => {
    if (isSameSearch(urlSearch, lastWritten.current)) {
      return;
    }
    const next = stateFromSearch(urlSearch);
    setViewState(next.view);
    setFilters(next.filters);
    setSearchInput(next.searchInput);
    setDate(next.date);
  }, [urlSearch]);

  useEffect(() => {
    setPage(1);
  }, [view, filters, search, date]);

  /** Switching between submissions and Not submitted drops a date range on the other view's date. */
  const setView = (next: TReviewView) => {
    const nextIsMissing = next === 'not_submitted';
    if (nextIsMissing !== isMissingView) {
      setDate({ field: defaultDateField(nextIsMissing) });
    }
    setViewState(next);
  };

  const setFilter = (key: TReviewListFilterKey) => (value: TReviewFilterValue) =>
    setFilters(prev => ({ ...prev, [key]: value }));

  const activeKeys = FILTERS_BY_VIEW[isMissingView ? 'missing' : 'submissions'];
  const hasActiveFilters =
    !!searchInput || !!date.from || !!date.to || activeKeys.some(key => filters[key].ids.length > 0);

  const clearFilters = () => {
    setFilters(prev => ({ ...prev, ...Object.fromEntries(activeKeys.map(key => [key, EMPTY_FILTER_VALUE])) }));
    setSearchInput('');
    setDate({ field: date.field });
  };

  const shared = {
    search,
    moduleIds: filters.module.ids,
    moduleMode: filters.module.mode,
    formIds: filters.form.ids,
    formMode: filters.form.mode,
    teacherIds: filters.teacher.ids,
    teacherMode: filters.teacher.mode,
  };
  const queueFilters: Omit<TReviewQueueParams, 'page' | 'perPage'> = isMissingView
    ? // Under Not submitted the queue still loads, as All, for the tile counts.
      { ...shared, status: 'all', reviewTime: [], reviewTimeMode: 'include', dateField: 'submitted' }
    : {
        ...shared,
        status: view,
        reviewTime: filters.reviewTime.ids as TReviewTimeState[],
        reviewTimeMode: filters.reviewTime.mode,
        dateField: date.field as TReviewQueueDateField,
        dateFrom: date.from,
        dateTo: date.to,
      };
  const missingFilters: Omit<TReviewMissingParams, 'page' | 'perPage'> = {
    ...shared,
    progress: filters.progress.ids as TReviewMissingProgress[],
    progressMode: filters.progress.mode,
    due: filters.due.ids as TReviewDueState[],
    dueMode: filters.due.mode,
    ...(isMissingView
      ? { dateField: date.field as TReviewMissingDateField, dateFrom: date.from, dateTo: date.to }
      : { dateField: 'due' as const }),
  };

  const queueParams: TReviewQueueParams = { ...queueFilters, page: isMissingView ? 1 : page, perPage: PER_PAGE };
  const missingParams: TReviewMissingParams = { ...missingFilters, page, perPage: PER_PAGE };
  const exportParams: TReviewExportParams = isMissingView
    ? { view: 'not_submitted', ...missingFilters }
    : { view: 'submissions', ...queueFilters };

  return {
    view,
    setView,
    isMissingView,
    filters,
    setFilter,
    setFilters,
    searchInput,
    setSearchInput,
    date,
    setDate,
    page,
    setPage,
    hasActiveFilters,
    clearFilters,
    queueParams,
    missingParams,
    exportParams,
  };
};
